import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/AppLayout"
import { Head, router, usePage } from "@inertiajs/react"
import { IconArrowLeft, IconPlus, IconTrash } from "@tabler/icons-react"
import { useState } from "react"

interface Customer {
  id: number
  name: string
}

interface ProjectOption {
  id: number
  name: string
}

interface Employee {
  id: number
  name: string
}

interface ExistingLineItem {
  id: number
  description: string
  quantity: number
  unit_price: number
  user_id: number | null
  discount_percentage: number | null
  tax_percentage: number | null
  position: number | null
}

interface InvoiceData {
  id: number
  invoice_number: string
  customer_id: number
  project_id: number | null
  invoice_date: string
  due_date: string | null
  currency: string
  notes: string | null
  invoice_pdf_url: string | null
  sent_to_accounting: boolean
  line_items: ExistingLineItem[]
}

interface LineItemRow {
  key: string
  id: number | null
  description: string
  quantity: string
  unit_price: string
  user_id: string
  discount_percentage: string
  tax_percentage: string
  _destroy: boolean
}

interface Props {
  [key: string]: unknown
  invoice: InvoiceData
  customers: Customer[]
  projects: ProjectOption[]
  employees: Employee[]
}

function generateKey() {
  return Math.random().toString(36).slice(2, 10)
}

function emptyLineItem(): LineItemRow {
  return {
    key: generateKey(),
    id: null,
    description: "",
    quantity: "1",
    unit_price: "",
    user_id: "",
    discount_percentage: "",
    tax_percentage: "",
    _destroy: false,
  }
}

function existingToRow(li: ExistingLineItem): LineItemRow {
  return {
    key: generateKey(),
    id: li.id,
    description: li.description,
    quantity: String(li.quantity),
    unit_price: String(li.unit_price),
    user_id: li.user_id ? String(li.user_id) : "",
    discount_percentage: li.discount_percentage ? String(li.discount_percentage) : "",
    tax_percentage: li.tax_percentage ? String(li.tax_percentage) : "",
    _destroy: false,
  }
}

function calcLineTotal(item: LineItemRow) {
  const qty = Number.parseFloat(item.quantity) || 0
  const price = Number.parseFloat(item.unit_price) || 0
  const subtotal = qty * price
  const disc = Number.parseFloat(item.discount_percentage) || 0
  const discAmount = subtotal * (disc / 100)
  const afterDisc = subtotal - discAmount
  const tax = Number.parseFloat(item.tax_percentage) || 0
  const taxAmount = afterDisc * (tax / 100)
  return afterDisc + taxAmount
}

function calcTotals(items: LineItemRow[]) {
  let subtotal = 0
  let totalDiscount = 0
  let totalTax = 0

  for (const item of items) {
    if (item._destroy) continue
    const qty = Number.parseFloat(item.quantity) || 0
    const price = Number.parseFloat(item.unit_price) || 0
    const lineSubtotal = qty * price
    const disc = Number.parseFloat(item.discount_percentage) || 0
    const discAmount = lineSubtotal * (disc / 100)
    const afterDisc = lineSubtotal - discAmount
    const tax = Number.parseFloat(item.tax_percentage) || 0
    const taxAmount = afterDisc * (tax / 100)
    subtotal += lineSubtotal
    totalDiscount += discAmount
    totalTax += taxAmount
  }

  return {
    subtotal,
    discount: totalDiscount,
    tax: totalTax,
    total: subtotal - totalDiscount + totalTax,
  }
}

export default function InvoiceEdit() {
  const { invoice, customers, projects, employees } = usePage<Props>().props

  const [data, setData] = useState({
    customer_id: String(invoice.customer_id),
    project_id: invoice.project_id ? String(invoice.project_id) : "",
    invoice_date: invoice.invoice_date || "",
    due_date: invoice.due_date || "",
    currency: invoice.currency,
    notes: invoice.notes || "",
    invoice_pdf_url: invoice.invoice_pdf_url || "",
    sent_to_accounting: invoice.sent_to_accounting,
  })

  const initialLineItems = invoice.line_items.length > 0 ? invoice.line_items.map(existingToRow) : [emptyLineItem()]

  const [lineItems, setLineItems] = useState<LineItemRow[]>(initialLineItems)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [processing, setProcessing] = useState(false)

  const currencySymbol = data.currency === "usd" ? "$" : "$U"
  const visibleLineItems = lineItems.filter((item) => !item._destroy)
  const totals = calcTotals(lineItems)
  const allErrors = Object.values(errors)

  function updateField<K extends keyof typeof data>(field: K, value: (typeof data)[K]) {
    setData((prev) => ({ ...prev, [field]: value }))
  }

  function addLineItem() {
    setLineItems((prev) => [...prev, emptyLineItem()])
  }

  function removeLineItem(key: string) {
    setLineItems((prev) => {
      const item = prev.find((i) => i.key === key)
      if (!item) return prev

      // If it's a persisted record, mark for destruction
      if (item.id) {
        return prev.map((i) => (i.key === key ? { ...i, _destroy: true } : i))
      }
      // If it's a new unsaved row, just remove it (unless it's the last visible one)
      const visible = prev.filter((i) => !i._destroy && i.key !== key)
      if (visible.length === 0) return prev
      return prev.filter((i) => i.key !== key)
    })
  }

  function updateLineItem(key: string, field: keyof LineItemRow, value: string) {
    setLineItems((prev) => prev.map((item) => (item.key === key ? { ...item, [field]: value } : item)))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)
    setErrors({})

    const invoiceLineItemsAttributes: Record<string, Record<string, unknown>> = {}
    lineItems.forEach((item, i) => {
      // Skip new empty rows that haven't been filled in
      if (!item.id && item.description.trim() === "" && !item._destroy) return

      const attrs: Record<string, unknown> = {
        description: item.description,
        quantity: Number.parseFloat(item.quantity) || 1,
        unit_price: Number.parseFloat(item.unit_price) || 0,
        user_id: item.user_id || null,
        discount_percentage: item.discount_percentage ? Number.parseFloat(item.discount_percentage) : null,
        tax_percentage: item.tax_percentage ? Number.parseFloat(item.tax_percentage) : null,
        position: i,
        _destroy: item._destroy,
      }

      if (item.id) {
        attrs.id = item.id
      }

      invoiceLineItemsAttributes[String(i)] = attrs
    })

    const payload = {
      ...data,
      invoice_line_items_attributes: invoiceLineItemsAttributes,
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    router.put(`/invoices/${invoice.id}`, { invoice: payload } as any, {
      onFinish: () => setProcessing(false),
      onError: (errs) => setErrors(errs),
    })
  }

  function formatCurrency(amount: number) {
    return `${currencySymbol}${amount.toFixed(2)}`
  }

  return (
    <>
      <Head title={`Edit ${invoice.invoice_number} - Operations Center`} />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/invoices">Invoices</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href={`/invoices/${invoice.id}`}>{invoice.invoice_number}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Edit</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href={`/invoices/${invoice.id}`}>
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <h1 className="text-lg font-semibold tracking-tight">Edit Invoice</h1>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Invoice Details */}
            <Card>
              <CardHeader>
                <CardTitle>Invoice Details</CardTitle>
              </CardHeader>
              <CardContent>
                {allErrors.length > 0 && (
                  <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                    <ul className="space-y-1 text-sm text-destructive">
                      {allErrors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="space-y-5">
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Customer</Label>
                      <Select value={data.customer_id} onValueChange={(v) => updateField("customer_id", v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select customer" />
                        </SelectTrigger>
                        <SelectContent>
                          {customers.map((c) => (
                            <SelectItem key={c.id} value={String(c.id)}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Project</Label>
                      <Select value={data.project_id} onValueChange={(v) => updateField("project_id", v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select project (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          {projects.map((p) => (
                            <SelectItem key={p.id} value={String(p.id)}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor="invoice_date">Invoice Date</Label>
                      <Input
                        id="invoice_date"
                        type="date"
                        value={data.invoice_date}
                        onChange={(e) => updateField("invoice_date", e.target.value)}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="due_date">Due Date</Label>
                      <Input
                        id="due_date"
                        type="date"
                        value={data.due_date}
                        onChange={(e) => updateField("due_date", e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Currency</Label>
                      <Select value={data.currency} onValueChange={(v) => updateField("currency", v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="usd">USD</SelectItem>
                          <SelectItem value="uyu">UYU</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <Label htmlFor="invoice_pdf_url">Invoice PDF URL</Label>
                    <Input
                      id="invoice_pdf_url"
                      type="url"
                      value={data.invoice_pdf_url}
                      onChange={(e) => updateField("invoice_pdf_url", e.target.value)}
                      placeholder="https://..."
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="sent_to_accounting"
                      checked={data.sent_to_accounting}
                      onCheckedChange={(checked) => updateField("sent_to_accounting", checked === true)}
                    />
                    <Label htmlFor="sent_to_accounting" className="font-normal">
                      Sent to accounting
                    </Label>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      rows={3}
                      value={data.notes}
                      onChange={(e) => updateField("notes", e.target.value)}
                      placeholder="Optional notes..."
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Line Items */}
            <Card>
              <CardHeader>
                <CardTitle>Line Items</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[200px]">Description</TableHead>
                        <TableHead className="min-w-[150px]">Employee</TableHead>
                        <TableHead className="w-[90px] text-right">Qty</TableHead>
                        <TableHead className="w-[120px] text-right">Unit Price</TableHead>
                        <TableHead className="w-[90px] text-right">Disc %</TableHead>
                        <TableHead className="w-[90px] text-right">Tax %</TableHead>
                        <TableHead className="w-[110px] text-right">Total</TableHead>
                        <TableHead className="w-[50px]" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visibleLineItems.map((item) => (
                        <TableRow key={item.key}>
                          <TableCell className="align-top p-2">
                            <Input
                              value={item.description}
                              onChange={(e) => updateLineItem(item.key, "description", e.target.value)}
                              placeholder="Description"
                              className="h-9"
                            />
                          </TableCell>
                          <TableCell className="align-top p-2">
                            <Select value={item.user_id} onValueChange={(v) => updateLineItem(item.key, "user_id", v)}>
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Optional" />
                              </SelectTrigger>
                              <SelectContent>
                                {employees.map((emp) => (
                                  <SelectItem key={emp.id} value={String(emp.id)}>
                                    {emp.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="align-top p-2">
                            <Input
                              type="number"
                              min="0.01"
                              step="0.01"
                              value={item.quantity}
                              onChange={(e) => updateLineItem(item.key, "quantity", e.target.value)}
                              className="h-9 text-right tabular-nums"
                            />
                          </TableCell>
                          <TableCell className="align-top p-2">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.unit_price}
                              onChange={(e) => updateLineItem(item.key, "unit_price", e.target.value)}
                              placeholder="0.00"
                              className="h-9 text-right tabular-nums"
                            />
                          </TableCell>
                          <TableCell className="align-top p-2">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={item.discount_percentage}
                              onChange={(e) => updateLineItem(item.key, "discount_percentage", e.target.value)}
                              placeholder="0"
                              className="h-9 text-right tabular-nums"
                            />
                          </TableCell>
                          <TableCell className="align-top p-2">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={item.tax_percentage}
                              onChange={(e) => updateLineItem(item.key, "tax_percentage", e.target.value)}
                              placeholder="0"
                              className="h-9 text-right tabular-nums"
                            />
                          </TableCell>
                          <TableCell className="align-top p-2 text-right">
                            <div className="flex h-9 items-center justify-end text-sm font-medium tabular-nums">
                              {formatCurrency(calcLineTotal(item))}
                            </div>
                          </TableCell>
                          <TableCell className="align-top p-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9 text-muted-foreground hover:text-destructive"
                              onClick={() => removeLineItem(item.key)}
                              disabled={visibleLineItems.length <= 1}
                            >
                              <IconTrash className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="border-t px-4 py-3">
                  <Button type="button" variant="ghost" size="sm" onClick={addLineItem}>
                    <IconPlus className="mr-2 h-4 w-4" />
                    Add Line Item
                  </Button>
                </div>
              </CardContent>

              {/* Totals */}
              <CardFooter className="justify-end border-t">
                <dl className="w-full max-w-xs space-y-1.5 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Subtotal</dt>
                    <dd className="tabular-nums">{formatCurrency(totals.subtotal)}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Discounts</dt>
                    <dd className="tabular-nums text-muted-foreground">-{formatCurrency(totals.discount)}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Taxes</dt>
                    <dd className="tabular-nums text-muted-foreground">{formatCurrency(totals.tax)}</dd>
                  </div>
                  <Separator className="!my-2" />
                  <div className="flex items-center justify-between font-semibold">
                    <dt>Total</dt>
                    <dd className="tabular-nums">{formatCurrency(totals.total)}</dd>
                  </div>
                </dl>
              </CardFooter>
            </Card>

            {/* Submit */}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" asChild>
                <a href={`/invoices/${invoice.id}`}>Cancel</a>
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </>
  )
}

InvoiceEdit.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
